import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DeploymentContext } from '@Common';
import { SearchService } from '../search/search.service.js';
import { WebAnalyticsService } from '../web-analytics/web.analytics.js';
import { LocalizedTextIds, Mode } from 'company-finder-common';

export interface Breadcrumb {
	id: string;
	name: string;
	action: () => unknown;
	extraAction: () => unknown;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbsService {
	public breadcrumbs: Breadcrumb[] = [];
	public rootBreadcrumb: Breadcrumb;

	constructor(
		private deploymentContext: DeploymentContext,
		public router: Router,
		private searchService: SearchService,
		private webAnalyticsService: WebAnalyticsService
	) {
		this.rootBreadcrumb = {
			id: 'root',
			name: 'Explore',
			action: () => {
				this.webAnalyticsService.trackBreadcrumbNavigation(
					this.rootBreadcrumb.name,
					this.router
				);

				// ADJQ-210: based on config value, optionally always clear filter if navigating to the Explore
				if (
					this.deploymentContext.rawConfig.behavior
						.clearFilterUponNavigateToExplore
				) {
					searchService.clearAllFilters(
						this.deploymentContext.currentMode === Mode.Asset
					);
				}

				// Handle navigation to the root from other routes.
				this.router.navigate(['/']);

				// NOTE: This breadcrumb doesn't have access to the MainComponent's
				// methods, but relies on NavigationStart to clear search results.
				// The MainComponent will augment this breadcrumb with a call to
				// clearSearchResults() in its action to handle transitions from
				// overview to search results.

				// Hack off any other crumbs after this one.
				this.setCurrentBreadcrumb(this.rootBreadcrumb);
				if (this.rootBreadcrumb.extraAction) {
					this.rootBreadcrumb.extraAction();
				}
			},
			extraAction: undefined,
		};
	}

	public setDetailsBreadcrumb(
		id: string,
		name: string,
		mode: Mode = undefined,
		requireSearch: boolean = true
	) {
		this.breadcrumbs = [this.rootBreadcrumb];

		if (requireSearch) {
			this.breadcrumbs.push(this.getSearchBreadcrumb(mode));
		}

		this.breadcrumbs.push({
			id,
			name,
			action: undefined,
			extraAction: undefined,
		});
	}

	private getSearchTitle(mode: Mode): string {
		return this.searchService.isAllItems
			? this.deploymentContext.Loc(
					LocalizedTextIds.AllCompanies,
					mode === Mode.Company
						? LocalizedTextIds.Companies
						: LocalizedTextIds.Assets
			  )
			: this.deploymentContext.Loc(
					LocalizedTextIds.BreadcrumbsSearchResults
			  );
	}

	private getSearchBreadcrumb(mode: Mode): Breadcrumb {
		return {
			id: 'search',
			name: this.getSearchTitle(mode),
			action: async () => {
				this.webAnalyticsService.trackBreadcrumbNavigation(
					this.getSearchTitle(mode),
					this.router
				);
				// Hack off any other crumbs after this one.
				this.setCurrentBreadcrumb(this.getSearchBreadcrumb(mode));
				// Handle navigation to the root from other routes.
				this.deploymentContext.currentMode = mode;
				this.searchService.navigateToSearchResults();
			},
			extraAction: undefined,
		};
	}

	public searchPageOffset: number;

	private setCurrentBreadcrumb(breadcrumb: Breadcrumb): void {
		const index = this.findBreadcrumbIndex(breadcrumb);
		if (index > -1) {
			this.breadcrumbs.length = index + 1;
		}
	}

	public setSearchPageOffset(offset: number): void {
		this.searchPageOffset = offset;
	}

	private findBreadcrumbIndex(breadcrumb: Breadcrumb): number {
		return this.breadcrumbs.findIndex((bc) => bc.id === breadcrumb.id);
	}
}
